import logger from "../utils/logger";
/**
 * OneSignal Push Notifications Service
 *
 * Handles initialization, subscription, and management of push notifications
 * using OneSignal Web SDK.
 */

import OneSignal from 'react-onesignal';

const ONESIGNAL_APP_ID = import.meta.env.VITE_ONESIGNAL_APP_ID;
const ONESIGNAL_SAFARI_WEB_ID = import.meta.env.VITE_ONESIGNAL_SAFARI_WEB_ID;

let isInitialized = false;

/**
 * Initialize OneSignal SDK
 * Should be called once on app load
 */
export async function initializeOneSignal() {
  if (isInitialized) {
    logger.log('[OneSignal] Already initialized');
    return;
  }

  if (!ONESIGNAL_APP_ID) {
    console.warn('[OneSignal] App ID not configured. Push notifications disabled.');
    return;
  }

  logger.log('[OneSignal] Starting initialization with App ID:', ONESIGNAL_APP_ID);

  try {
    logger.log('[OneSignal] Calling OneSignal.init()...');

    // Add 5 second timeout
    await Promise.race([
      OneSignal.init({
        appId: ONESIGNAL_APP_ID,
        safari_web_id: ONESIGNAL_SAFARI_WEB_ID,
        notifyButton: {
          enable: false, // We use custom UI in Settings
        },
        allowLocalhostAsSecureOrigin: true, // For local development
        serviceWorkerPath: '/OneSignalSDKWorker.js',
        serviceWorkerParam: { scope: '/' },
      }),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('OneSignal init timeout after 5s')), 5000)
      )
    ]);

    isInitialized = true;
    logger.log('[OneSignal] ✅ Initialized successfully!');

    // Log subscription changes
    OneSignal.on('subscriptionChange', (isSubscribed) => {
      logger.log('[OneSignal] Subscription changed:', isSubscribed);
    });

    OneSignal.on('notificationDisplay', (event) => {
      logger.log('[OneSignal] Notification displayed:', event);
    });

  } catch (error) {
    // Timeout/blocked by ad blocker — not actionable, log quietly
    logger.log('[OneSignal] Init skipped:', error.message);
  }
}

/**
 * Subscribe user to push notifications
 * @param {string} userId - Supabase user ID to link with OneSignal
 * @returns {Promise<boolean>} - True if subscribed successfully
 */
export async function subscribeUser(userId) {
  logger.log('[OneSignal] subscribeUser called. isInitialized:', isInitialized);

  if (!isInitialized) {
    console.error('[OneSignal] ❌ Not initialized! Call initializeOneSignal() first.');
    console.error('[OneSignal] Attempting to initialize now...');

    // Try to initialize if not already done
    await initializeOneSignal();

    if (!isInitialized) {
      console.error('[OneSignal] Still not initialized after attempt. Aborting subscribe.');
      return false;
    }
  }

  try {
    // Request notification permission
    const permission = await OneSignal.getNotificationPermission();

    if (permission === 'denied') {
      console.warn('[OneSignal] Permission denied by user');
      return false;
    }

    if (permission !== 'granted') {
      // Show native permission prompt
      await OneSignal.showNativePrompt();
    }

    // Wait for permission to be granted
    const newPermission = await OneSignal.getNotificationPermission();
    if (newPermission !== 'granted') {
      console.warn('[OneSignal] Permission not granted');
      return false;
    }

    // Set external user ID (Supabase user ID)
    // This allows sending notifications to specific users from backend
    await OneSignal.setExternalUserId(userId);

    // Subscribe to push
    await OneSignal.setSubscription(true);

    logger.log('[OneSignal] User subscribed:', userId);
    return true;

  } catch (error) {
    console.error('[OneSignal] Subscription failed:', error);
    return false;
  }
}

/**
 * Unsubscribe user from push notifications
 * @returns {Promise<boolean>} - True if unsubscribed successfully
 */
export async function unsubscribeUser() {
  if (!isInitialized) {
    console.error('[OneSignal] Not initialized');
    return false;
  }

  try {
    await OneSignal.setSubscription(false);
    await OneSignal.removeExternalUserId();

    logger.log('[OneSignal] User unsubscribed');
    return true;

  } catch (error) {
    console.error('[OneSignal] Unsubscribe failed:', error);
    return false;
  }
}

/**
 * Check if user is currently subscribed
 * @returns {Promise<boolean>} - True if subscribed
 */
export async function isSubscribed() {
  if (!isInitialized) {
    return false;
  }

  try {
    const isPushEnabled = await OneSignal.isPushNotificationsEnabled();
    return isPushEnabled;
  } catch (error) {
    console.error('[OneSignal] Failed to check subscription status:', error);
    return false;
  }
}

/**
 * Get notification permission status
 * @returns {Promise<'default'|'granted'|'denied'>}
 */
export async function getPermissionStatus() {
  if (!isInitialized) {
    return 'default';
  }

  try {
    return await OneSignal.getNotificationPermission();
  } catch (error) {
    console.error('[OneSignal] Failed to get permission status:', error);
    return 'default';
  }
}

/**
 * Get OneSignal Player ID (device identifier)
 * @returns {Promise<string|null>}
 */
export async function getPlayerId() {
  if (!isInitialized) {
    return null;
  }

  try {
    const playerId = await OneSignal.getUserId();
    return playerId;
  } catch (error) {
    console.error('[OneSignal] Failed to get player ID:', error);
    return null;
  }
}

/**
 * Send a tag to OneSignal for user segmentation
 * Example: { subscription_plan: 'pro', properties_count: 3 }
 * @param {Object} tags - Key-value pairs to tag user with
 */
export async function sendTags(tags) {
  if (!isInitialized) {
    console.error('[OneSignal] Not initialized');
    return;
  }

  try {
    await OneSignal.sendTags(tags);
    logger.log('[OneSignal] Tags sent:', tags);
  } catch (error) {
    console.error('[OneSignal] Failed to send tags:', error);
  }
}

/**
 * Show native notification prompt
 * Use this for custom opt-in flow
 */
export async function showPrompt() {
  if (!isInitialized) {
    console.error('[OneSignal] Not initialized');
    return false;
  }

  try {
    await OneSignal.showNativePrompt();
    return true;
  } catch (error) {
    console.error('[OneSignal] Failed to show prompt:', error);
    return false;
  }
}
